import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TopOrder } from "../types/dashboard";

interface TopOrdersProps {
  orders: TopOrder[];
  businessType?: string;
}

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return dateStr;
  }
};

const getPaymentStatus = (received: number, total: number) => {
  if (received >= total)
    return {
      label: "Paid",
      color: "text-emerald-600 bg-emerald-50 border-emerald-200",
    };
  if (received > 0)
    return {
      label: "Partial",
      color: "text-amber-600 bg-amber-50 border-amber-200",
    };
  return {
    label: "Pending",
    color: "text-red-500 bg-red-50 border-red-200",
  };
};

const TopOrders: React.FC<TopOrdersProps> = ({ orders, businessType }) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!orders || orders.length === 0) {
    return <div className="rounded-2xl bg-white/70 backdrop-blur-xl border border-gray-200 p-6 text-center text-gray-400">No order insights available</div>;
  }

  const totalValue = orders.reduce((s, o) => s + o.total_amount, 0);

  return (
    <div className="relative rounded-2xl bg-white/70 backdrop-blur-xl border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)] overflow-hidden">
      {/* Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/10 pointer-events-none" />

      {/* Header */}
      <div className="relative px-6 pt-6 pb-4 flex justify-between items-start">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Top Orders</h2>
          <p className="text-xs text-gray-400 mt-1">Highest value transactions</p>
        </div>

        <div className="text-right">
          <p className="text-lg font-semibold text-gray-900">{totalValue.toFixed(2)} QAR</p>
          <p className="text-xs text-gray-400">Total</p>
        </div>
      </div>

      <div className="border-t border-gray-100/80" />

      {/* Orders */}
      <div className="px-4 py-4 space-y-2">
        {orders.map((order, index) => {
          const status = getPaymentStatus(order.received, order.total_amount);

          const outstanding = Math.max(0, order.total_amount - order.received);
          const isExpanded = expanded === order.order_id;

          const pct = order.total_amount > 0 ? Math.min(100, Math.round((order.received / order.total_amount) * 100)) : 0;

          // Check if business is restaurant
          const isRestaurant = businessType === "Restaurant";

          return (
            <motion.div
              key={order.order_id}
              layout
              whileHover={{ scale: 1.01 }}
              className={`rounded-xl border transition-all cursor-pointer ${isExpanded ? "bg-blue-50/60 border-blue-200" : "bg-white/60 border-gray-200 hover:shadow-sm"}`}
              onClick={() => setExpanded(isExpanded ? null : order.order_id)}
            >
              {/* Main Row */}
              <div className="flex items-center gap-4 px-4 py-3">
                {/* Rank */}
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold ${
                    index === 0 ? "bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {index + 1}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-mono font-semibold text-gray-700 truncate">#{order.order_id}</p>

                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${status.color}`}>{status.label}</span>
                  </div>

                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatDate(order.order_date)} · {order.order_time}
                  </p>
                </div>

                {/* Amount */}
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{order.total_amount.toFixed(2)} QAR</p>
                  <p className="text-xs text-gray-400">{pct}% collected</p>
                </div>

                {/* Arrow */}
                <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} className="text-gray-400">
                  ▼
                </motion.div>
              </div>

              {/* Progress */}
              <div className="px-4 pb-3">
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} className="h-full bg-gradient-to-r from-indigo-400 to-indigo-500" />
                </div>
              </div>

              {/* Expand */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="px-4 pb-4">
                    <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Total</p>
                        <p className="font-semibold text-gray-800">{order.total_amount.toFixed(2)} QAR</p>
                      </div>

                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Received</p>
                        <p className="font-semibold text-emerald-600">{order.received.toFixed(2)} QAR</p>
                      </div>

                      {/* Conditionally render Outstanding - hide for restaurants */}
                      {!isRestaurant && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase">Outstanding</p>
                          <p className={`font-semibold ${outstanding > 0 ? "text-red-500" : "text-gray-400"}`}>{outstanding.toFixed(2)} QAR</p>
                        </div>
                      )}

                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Progress</p>
                        <p className="font-semibold text-gray-800">{pct}%</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default TopOrders;